import React, { useEffect, useRef } from 'react'
import { Box, Flex, Input, Skeleton, Text } from '@chakra-ui/react'

interface ModalInputSelectProps {
  label?: string
  placeholder: string
  options: {
    label: string
    value: string
  }[]
  value: { label: string; value: string } | null
  isLoading?: boolean
  isDisabled?: boolean
  search: string
  onSearchChange: (value: string) => void
  onChange: (value: { label: string; value: string } | null) => void
}

const ModalInputSelect: React.FC<ModalInputSelectProps> = ({
  label,
  placeholder,
  options = [],
  value,
  isLoading,
  isDisabled,
  search,
  onSearchChange,
  onChange,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const isOpen = search.length > 0 && !value

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        onSearchChange('')
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [onSearchChange])

  return (
    <Box width="100%" pos="relative" ref={containerRef}>
      {label && (
        <Text textStyle="l-xs" color="solid.500" mb={1}>
          {label}
        </Text>
      )}
      <Input
        isDisabled={isDisabled}
        value={value ? value.label : search}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder={placeholder}
        bg={isDisabled ? 'solid.100' : 'white'}
        onFocus={() => {
          if (value) {
            onSearchChange('')
            onChange(null)
          }
        }}
      />
      <Box
        display={isOpen ? 'block' : 'none'}
        w="100%"
        minH="120px"
        bg="white"
        zIndex="1"
        pos="absolute"
        top="66px"
        borderRadius="8px"
        borderColor="solid.100"
        boxShadow={'0px 2px 8px -2px #00000014, 0px 2px 14px -3px #0000001F'}
        borderWidth="1px"
        overflow="hidden"
      >
        {options.length > 0 && (
          <>
            {options.map((option) => (
              <Flex
                key={option.value}
                alignItems="center"
                gap="12px"
                h="40px"
                px="12px"
                cursor="pointer"
                _hover={{
                  bg: 'solid.25',
                }}
                _active={{
                  bg: 'solid.50',
                }}
                transition="0.2s ease-out"
                onClick={() => onChange(option)}
              >
                <Text textStyle="l-s">{option.label}</Text>
              </Flex>
            ))}
          </>
        )}

        {!isLoading && options.length === 0 && (
          <Flex alignItems="center" justifyContent="center" h="120px">
            <Text>No results</Text>
          </Flex>
        )}

        {isLoading &&
          [1, 2, 3, 4].map((_, index) => (
            <Flex key={index} alignItems="center" px="12px" h="40px">
              <Skeleton w="140px" h="20px" />
            </Flex>
          ))}
      </Box>
    </Box>
  )
}

export default ModalInputSelect
